import * as React from 'react';
import classnames from 'classnames';
import ReactSelect, { ActionMeta, Props as ReactSelectProps, SelectInstance, components } from 'react-select'
import { SelectComponentsConfig } from "react-select/dist/declarations/src/components";
import Tooltip from "../Tooltip";
import { hocFieldWrapper } from "../FieldWrapper";
import { loadingMessage, noOptionsMessage } from "./helpers";
import customStyles from './styles';
import { isFieldsEqual } from "../../helpers/isFieldsEqual";
import { Value, Option } from "../../helpers/arrayToDict";
import dispatchCloseEvent from "../Modals/helpers/dispatchCloseEvent";
import style from './Select.scss';
import { Group } from "../EntitySelect/EntitySelect";

const finalComponents: SelectComponentsConfig<any, any, any> = {
  SingleValue: (props) => {
    const { children } = props;
    const elRef = React.useRef<HTMLDivElement>();
    const [overflow, setOverflow] = React.useState(false)
    React.useEffect(() => {
      const el = elRef.current
      setOverflow(el.scrollWidth > el.clientWidth);
    }, [children]);

    let result = <div ref={elRef} className={`${props.selectProps.className}__single-value`}>{children}</div>;
    if (overflow) result = <Tooltip content={children}>{result}</Tooltip>;
    return result

  },
}

const compareValues = (v1: any, v2: any) => {
  const o1 = v1?.original;
  const o2 = v2?.original;
  if (o1 || o2) return o1 === o2;
  return v1 === v2;
}

const portalTarget = document.getElementById('popups');
const compareProps = ['value', 'options'] as const;

export type Props<V extends Value = Value,
  O extends Option<V> | Group<V> = Option<V> | Group<V>,
  IsMulti extends boolean = false> = Omit<ReactSelectProps<O, IsMulti>, 'isMulti' | 'value' | 'onChange'> & {
  isMulti?: boolean,
  value?: any | any[],
  onChange?: (value: any, meta?: ActionMeta<any>) => any,
  forwardedRef?: React.Ref<SelectInstance>,
  rawValue?: boolean,
  findValue?: (value: V, options: Option<V>[]) => any
  grouped?: boolean,
  disabled?: boolean,
};
type State = {
  value: any,
};

class Select extends React.PureComponent<Props, State> {
  state: State = {
    value: this.getValue(),
  };
  isOpen = false;
  foundValue: any = null;

  constructor(props: Props) {
    super(props);
    this.findValue = this.findValue.bind(this);
  }

  componentDidUpdate(prevProps: Props) {
    if (!isFieldsEqual(prevProps, this.props, compareProps)) {
      const value = this.getValue();
      this.setState({ value });
      if (this.props.findValue && value !== this.props.value) this.onChange(value, null);
    }
  }

  findValue(value: any) {
    const { grouped, options, rawValue, isMulti, findValue } = this.props;
    if (grouped) {
      if (rawValue) return value;
      for (let i = 0; i < options.length; i++) {
        const group = options[i] as Group;
        const v = group.options.find((o) => o.value === value);
        if (v) return v;
      }
      return null;
    }
    if (findValue && !compareValues(value, this.foundValue)) {
      const searchValue = value ? rawValue ? value.original : value : null;
      if (searchValue) {
        const foundValue = findValue(searchValue, options as Option[]) || null
        this.foundValue = foundValue;
        return foundValue;
      }
      this.foundValue = null;
      return;
    }
    if (rawValue) return value;
    return options.find((o: any) => o.value === value) || null;
    // return options.find((o: any) => rawValue ? o.original === value : o.value === value) || null;
  }

  getValue() {
    const { value, options } = this.props;
    if (!Array.isArray(options)) return null;
    return Array.isArray(value)
      ? value.map(this.findValue).filter(Boolean)
      : this.findValue(value);
  }

  extractValue = (value: any) => {
    return this.props.rawValue ? value : value.value;
  }

  onChange = (value: any, action: ActionMeta<any>) => {
    const { onChange } = this.props;
    const finalValue = Array.isArray(value)
      ? value.map(this.extractValue)
      : value
        ? this.extractValue(value)
        : null;
    onChange && onChange(finalValue, action);
  };

  onOpen = () => {
    if (this.props.onMenuOpen) this.props.onMenuOpen();
    this.isOpen = true;
  }

  onClose = () => {
    if (this.props.onMenuClose) this.props.onMenuClose();
    if (!this.isOpen) return;
    this.isOpen = false;
    dispatchCloseEvent();
  }

  render() {
    const { className, placeholder, forwardedRef, disabled, isDisabled, ...selectProps } = this.props;
    return (
      <ReactSelect
        ref={forwardedRef}
        className={classnames(style.select, className)}
        placeholder={placeholder || ''}
        components={finalComponents}
        classNamePrefix={style.select}
        styles={customStyles}

        menuPortalTarget={portalTarget}
        menuPosition="absolute"
        menuPlacement="auto"
        minMenuHeight={300}
        maxMenuHeight={300}

        loadingMessage={loadingMessage}
        noOptionsMessage={noOptionsMessage}
        // menuPosition="fixed"
        // styles={styles}
        // menuIsOpen
        {...selectProps}
        isDisabled={typeof disabled === 'boolean' ? disabled : isDisabled}
        value={this.state.value}
        onChange={this.onChange}
        onMenuOpen={this.onOpen}
        onMenuClose={this.onClose}
        // menuIsOpen={true}
      />
    );
  }
}

export const SelectRef = React.forwardRef<SelectInstance, Props>((props, ref) => {
  return <Select forwardedRef={ref} {...props} />;
});
SelectRef.displayName = 'Select';

export default hocFieldWrapper(SelectRef);
